import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Overlay,
  ModalContainer,
  ModalContent,
  Title,
  Subtitle,
  Label,
  Input,
  PasswordWrapper,
  GenerateButton,
  ButtonGroup,
  CancelButton,
  ShareButton,
  PasswordRow,
  PasswordInput,
  BackButton,
  SpanTitle,
  StyledDatePicker,
  ExpiryText,
  EyeIconWrapper,
  HorizondalLine,
  AccessListSection,
  AccessUser,
  RevokeLink,
  SubLabel,
  ErrorText,
  ErrorIconWrapper,
  DisabledPasswordInput,
} from "./ShareFileModalStyled";
import { ArrowsClockwise, Copy, Eye, EyeSlash, X } from "@phosphor-icons/react";
import { DatePickerDropdownStyles } from "../DatePicker";
import dayjs from "dayjs";
import { dateFormatList } from "../../utils/constants";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchExternalShareDetails,
  getGeneratedPassword,
  revokeExternalShare,
  submitExternalShare,
} from "../../slice/MatterDetailsSlice";
import { ErrorIcon, WarningIcon } from "../../assets/icons";
import { validateExternalShare } from "../../utils/ESignModalValidation";
import Loader from "../Loader/Loader";
import { MessageModal } from "../common/Modals";

const ShareFileModal = ({ isOpen, onClose, document_id, handleSnackbar }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    expiryDate: "",
  });
  const [isLoading, setisLoading] = useState(false);

  const dispatch = useDispatch();
  const { externalShareDetails } = useSelector((state) => state.matterDetails);
  const isExternalShareEnabled = useSelector(
    (state) => state.featureFlags.isExternalShareEnabled
  );
  useEffect(() => {
    if (document_id) {
      getExternalShareDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document_id]);

  const getExternalShareDetails = () => {
    dispatch(fetchExternalShareDetails({ document_id }))
      .then((response) => {
        console.log("response", response.payload.data);
      })
      .catch((error) => {
        console.log("eroor", error);
      });
  };

  const generatePassword = () => {
    dispatch(getGeneratedPassword())
      .then((response) => {
        setPassword(response?.payload?.data?.password);
      })
      .catch((error) => {
        console.log("eroor", error);
      });
  };

  const handleSubmit = () => {
    const { externalShareErrors, hasError } = validateExternalShare({
      email,
      password,
      expiryDate,
      accessList: externalShareDetails?.accessList,
    });

    setErrors(externalShareErrors);

    if (hasError) return;
    setisLoading(true);

    const payload = {
      document: document_id,
      recipient_emails: email,
      password: externalShareDetails?.password || password,
      expires_at:
        externalShareDetails?.expirydate ||
        dayjs(expiryDate).format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };

    dispatch(submitExternalShare(payload))
      .unwrap()
      .then(() => {
        handleSnackbar(
          `'${externalShareDetails?.documentname}' has been shared successfully`,
          true,
          false
        );
        onClose();
      })
      .catch(() => {
        handleSnackbar("Something went wrong", false, false);
      })
      .finally(() => {
        setisLoading(false);
      });
  };

  if (!isOpen) return null;

  const handleCopy = () => {
    navigator.clipboard
      .writeText(externalShareDetails?.password || password)
      .then(() => {
        handleSnackbar("Password copied to clipboard", true);
      })
      .catch((err) => {
        console.error("Failed to copy password:", err);
        handleSnackbar("Failed to copy password");
      });
  };

  const handleRevoke = () => {
    dispatch(
      revokeExternalShare({ document_id: externalShareDetails?.documentId })
    ).then((response) => {
      if (response?.payload?.status === 200) {
        handleSnackbar(response?.payload?.data?.message, true, false);
        setShowRevokeModal(false);
        onClose();
      } else {
        handleSnackbar("Failed to revoke share", false, false);
      }
    }).catch(() => {
      handleSnackbar("Something went wrong", false, false);
    });
  };

  return (
    <Overlay>
      {isExternalShareEnabled ? (
        <ModalContainer>
          <ModalContent>
            <Title>
              Share File Externally -{" "}
              <SpanTitle>{externalShareDetails?.documentname}</SpanTitle>
            </Title>
            <Subtitle>People with the link can view and download file</Subtitle>
            <Input
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={Boolean(errors.email)}
            />
            {errors.email && (
              <ErrorText>
                <ErrorIcon />
                {errors.email}
              </ErrorText>
            )}
            <SubLabel>Security</SubLabel>
            {externalShareDetails?.accessList?.length < 1 ? (
              <>
                <PasswordRow>
                  <Label small>Set Password (Optional)</Label>
                  <GenerateButton onClick={generatePassword}>
                    <ArrowsClockwise size={16} color="#0C53BB" />
                    Generate
                  </GenerateButton>
                </PasswordRow>

                <PasswordWrapper>
                  <PasswordInput
                    type="text"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={Boolean(errors.password)}
                  />
                  {password && (
                    <EyeIconWrapper>
                      <Copy size={16} color="#0C53BB" onClick={handleCopy} />
                    </EyeIconWrapper>
                  )}
                </PasswordWrapper>
                {errors.password && (
                  <ErrorText
                    style={{
                      alignItems: "flex-start",
                    }}
                  >
                    <ErrorIconWrapper>
                      <ErrorIcon />
                    </ErrorIconWrapper>
                    {errors.password}
                  </ErrorText>
                )}
                <Label small>File Expiry Date</Label>
                <>
                  <DatePickerDropdownStyles />
                  <StyledDatePicker
                    format={dateFormatList}
                    getPopupContainer={() =>
                      document.querySelector(".share-modal")
                    }
                    className={errors.expiryDate ? "error" : ""}
                    onChange={(date) => setExpiryDate(date)}
                    allowClear={false}
                    inputReadOnly
                    placeholder="Due Date"
                    value={expiryDate}
                    disabledDate={(current) => {
                      const today = dayjs().startOf("day");
                      const maxDate = dayjs().add(3, "year").endOf("day");
                      return current && (current < today || current > maxDate);
                    }}
                    error={Boolean(errors.expiryDate)}
                  />
                  {errors.expiryDate && (
                    <ErrorText>
                      <ErrorIcon />
                      {errors.expiryDate}
                    </ErrorText>
                  )}
                </>
              </>
            ) : (
              <>
                <PasswordRow>
                  <Label small>Password</Label>
                  <GenerateButton onClick={handleCopy}>
                    <Copy size={16} color="#0C53BB" />
                    Copy to Clipboard
                  </GenerateButton>
                </PasswordRow>
                <PasswordWrapper>
                  <DisabledPasswordInput
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={externalShareDetails?.password}
                  />
                  <EyeIconWrapper
                    onClick={() => {
                      setShowPassword(!showPassword);
                    }}
                  >
                    {showPassword ? <Eye size={16} /> : <EyeSlash size={16} />}
                  </EyeIconWrapper>
                </PasswordWrapper>

                <ExpiryText>
                  File access is set to expire after{" "}
                  <span>
                    {dayjs(externalShareDetails?.expirydate).format(
                      "MM/DD/YYYY"
                    ) || "N/A"}
                  </span>
                </ExpiryText>

                <AccessListSection>
                  <PasswordRow>
                    <SubLabel>People with Access</SubLabel>
                    <RevokeLink onClick={() => setShowRevokeModal(true)}>
                      Revoke Access
                    </RevokeLink>
                  </PasswordRow>
                  <div>
                    {externalShareDetails?.accessList?.map((user) => (
                      <AccessUser key={user}>{user}</AccessUser>
                    ))}
                  </div>
                </AccessListSection>
              </>
            )}
            <HorizondalLine />
            <ButtonGroup>
              <CancelButton onClick={onClose}>Cancel</CancelButton>
              <ShareButton
                onClick={handleSubmit}
                disabled={
                  !(
                    email &&
                    (externalShareDetails?.expirydate || expiryDate)
                  )
                }
              >
                Share
              </ShareButton>
            </ButtonGroup>
          </ModalContent>
          <BackButton>
            <X size={20} onClick={onClose} />
          </BackButton>
        </ModalContainer>
      ) : (
        <MessageModal
          open={true}
          onClose={onClose}
          title={"Warning: External Share Disabled"}
          message={
            "External Share Email notifications are currently disabled. Please contact your admin."
          }
          buttonText={"Close"}
          icon={<WarningIcon />}
          onClick={onClose}
          variant="warning"
        />
      )}
      <ConfirmModal
        isOpen={showRevokeModal}
        title="Revoke Access?"
        message="This will disable access to the file for all recipients."
        onCancel={() => setShowRevokeModal(false)}
        onConfirm={handleRevoke}
      />
      {isLoading && <Loader message={"Sharing file..."} />}
    </Overlay>
  );
};

ShareFileModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  document_id: PropTypes.string.isRequired,
  handleSnackbar: PropTypes.func,
};

export default ShareFileModal;
